import { Inbox } from "@novu/react";
import type { InboxProps } from "@novu/react";
import type { Notification } from "@novu/js";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaBell } from "react-icons/fa";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { NOVU_APP_CODE } from "./consts";

dayjs.extend(relativeTime);

// Whistle brand orange for interactive/unread accents; the rest matches antd v5.
const INBOX_APPEARANCE: InboxProps["appearance"] = {
	variables: {
		colorPrimary: "#f15923",
		colorPrimaryForeground: "#ffffff",
		colorForeground: "rgba(0, 0, 0, 0.88)",
		colorSecondary: "#f5f5f5",
		colorSecondaryForeground: "rgba(0, 0, 0, 0.65)",
		colorBackground: "#ffffff",
		colorNeutral: "#f0f0f0",
		colorCounter: "#ff4d4f",
		colorCounterForeground: "#ffffff",
		colorShadow: "rgba(0, 0, 0, 0.08)",
		borderRadius: "8px",
		fontSize: "18px",
	},
	elements: {
		notificationImage: { display: "none" },
		notificationImageLoadingFallback: { display: "none" },
		popoverContent: { maxHeight: "480px" },
		preferences__button: { display: "none" },
		notificationSnooze__button: { display: "none" },
		notificationUnsnooze__button: { display: "none" },
		notificationArchive__button: { display: "none" },
		notificationUnarchive__button: { display: "none" },
		notification: { padding: "12px 16px" },
		// Timestamp is rendered inline on the subject line instead (renderSubject).
		notificationDate: { display: "none" },
	},
};

// Subject with the relative timestamp inline on the right, mail-client style.
// Inline styles only — this node is portaled into Novu's DOM.
const NotificationSubject = ({ notification }: { notification: Notification }) => (
	<span
		style={{
			display: "flex",
			alignItems: "baseline",
			justifyContent: "space-between",
			textAlign: "left",
			gap: 12,
			width: "100%",
		}}
	>
		<span>{notification.subject}</span>
		<span
			className="inbox-subject-time"
			style={{ fontSize: 12, fontWeight: 400, color: "rgba(0, 0, 0, 0.45)", whiteSpace: "nowrap" }}
		>
			{dayjs(notification.createdAt).fromNow()}
		</span>
	</span>
);

/**
 * Inline styles only: the live copy is portaled into Novu's shadow root,
 * which antd's injected stylesheets cannot reach. The static and live bells
 * must stay pixel-identical so the swap to Novu's trigger is invisible.
 */
const BellButton = ({ count = 0 }: { count?: number }) => (
	<span
		style={{
			position: "relative",
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			width: 32,
			height: 32,
			cursor: "pointer",
		}}
	>
		<FaBell style={{ fontSize: 20, color: "rgba(0, 0, 0, 0.65)" }} />
		{count > 0 && (
			<span
				style={{
					position: "absolute",
					top: 0,
					right: 0,
					minWidth: 16,
					height: 16,
					padding: "0 4px",
					borderRadius: 8,
					background: "#ff4d4f",
					color: "#ffffff",
					fontSize: 10,
					fontWeight: 600,
					lineHeight: "16px",
					textAlign: "center",
					boxShadow: "0 0 0 1px #ffffff",
					boxSizing: "border-box",
				}}
			>
				{count > 99 ? "99+" : count}
			</span>
		)}
	</span>
);

/**
 * Navbar notification bell. The bell renders from first paint; Novu's inbox
 * (popover, realtime badge) attaches behind it once the subscriber `uuid`
 * arrives and Novu's session handshake completes. Until then the bell is
 * visible but inert.
 */
export const NavbarInbox = () => {
	const navigate = useNavigate();
	const {
		currentUser: { uuid },
	} = useSelector(({ auth }) => auth);

	return (
		<div className="novu-inbox-wrapper" style={{ display: "flex", alignItems: "center", lineHeight: 0 }}>
			{uuid ? (
				<Inbox
					applicationIdentifier={NOVU_APP_CODE}
					subscriber={uuid}
					appearance={INBOX_APPEARANCE}
					routerPush={(path: string) => navigate(path)}
					preferencesFilter={{ tags: ["general", "admin", "security"] }}
					placement="bottom-end"
					placementOffset={8}
					renderBell={(unreadCount) => <BellButton count={unreadCount.total} />}
					renderSubject={(notification) => <NotificationSubject notification={notification} />}
				/>
			) : (
				<BellButton />
			)}
		</div>
	);
};
